import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, TrendingUp, CheckCircle, AlertTriangle, Search, User, Calendar, ChevronRight } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const CaseProgressWidget = ({ viewType, userId }) => {
  const [caseProgress, setCaseProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleCaseClick = (caseItem) => {
    // Navigate directly to case detail page
    navigate(`/cases/${caseItem.id}`);
  };

  const handleViewAllClick = () => {
    // Navigate to cases page
    navigate('/cases');
  };

  const fetchCaseProgress = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getCaseProgress(viewType, searchTerm);
      setCaseProgress(response.data.cases || []);
    } catch (error) {
      console.error('Error fetching case progress:', error);
      setCaseProgress([]);
    } finally {
      setLoading(false);
    }
  }, [viewType, searchTerm]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchCaseProgress();
    }, 300); // Debounce search

    return () => clearTimeout(delayDebounce);
  }, [fetchCaseProgress]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-yellow-600';
      case 'medium': return 'text-amber-600';
      case 'low': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-4">
            <div className="h-20 bg-gray-100 rounded"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Widget Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Briefcase className="h-5 w-5 text-gray-600 mr-2" />
            {viewType === 'my' ? 'My Case Progress' : 'Firm Case Progress'}
          </h3>
          <button
            onClick={handleViewAllClick}
            className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
          >
            View All Cases
          </button>
        </div>
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client, case, task, project, or assignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {caseProgress.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">No active cases</p>
            <p className="text-gray-500 text-sm">Start by creating a new case.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {caseProgress.slice(0, 10).map((caseItem) => (
              <div
                key={caseItem.id}
                onClick={() => handleCaseClick(caseItem)}
                className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  caseItem.needs_attention ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      {caseItem.needs_attention && (
                        <AlertTriangle className="h-4 w-4 text-red-500" title="No tasks defined" />
                      )}
                      {caseItem.case_number} - {caseItem.title}
                      <span className={`text-xs font-medium ${getPriorityColor(caseItem.priority)}`}>
                        {caseItem.priority.toUpperCase()}
                      </span>
                    </h4>
                    {caseItem.client_name && (
                      <p className="text-xs text-gray-500 mt-1">
                        Client: {caseItem.client_name}
                      </p>
                    )}
                    {viewType === 'firm' && caseItem.assigned_attorney && (
                      <p className="text-xs text-gray-500">
                        Attorney: {caseItem.assigned_attorney}
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-lg font-semibold text-gray-900">
                      {caseItem.progress_percentage}%
                    </div>
                    <div className="text-sm text-gray-500">
                      {caseItem.completed_tasks}/{caseItem.total_tasks} tasks
                    </div>
                    {caseItem.case_value && (
                      <div className="text-xs text-gray-400 mt-1">
                        ${caseItem.case_value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {!caseItem.needs_attention && (
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full"></div>
                      <div
                        className="absolute inset-0 bg-gray-200 rounded-full transition-all duration-300"
                        style={{ left: `${caseItem.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Next Task Info with Breadcrumb */}
                {caseItem.next_task ? (
                  <div
                    className="mb-2 p-3 bg-gray-50 border border-blue-100 rounded text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="font-medium text-gray-600 text-xs mb-2">Next up:</div>

                    {/* Breadcrumb Navigation */}
                    <div className="flex items-center flex-wrap gap-1 mb-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/cases/${caseItem.id}`);
                        }}
                        className="text-yellow-600 hover:text-yellow-700 hover:underline text-xs font-medium"
                      >
                        {caseItem.case_number}
                      </button>

                      <ChevronRight className="h-3 w-3 text-gray-400" />

                      {caseItem.next_task.project_id && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/projects/${caseItem.next_task.project_id}`);
                            }}
                            className="text-yellow-600 hover:text-yellow-700 hover:underline text-xs font-medium"
                          >
                            {caseItem.next_task.project}
                          </button>
                          <ChevronRight className="h-3 w-3 text-gray-400" />
                        </>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tasks/${caseItem.next_task.id}`);
                        }}
                        className="text-gray-900 font-semibold hover:text-yellow-700 hover:underline text-sm"
                      >
                        {caseItem.next_task.title}
                      </button>
                    </div>

                    {/* Task Details */}
                    <div className="flex items-center gap-3 text-xs text-yellow-600">
                      {caseItem.next_task.assignee && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {caseItem.next_task.assignee}
                        </span>
                      )}
                      {caseItem.next_task.due_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(caseItem.next_task.due_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ) : caseItem.needs_attention ? (
                  <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                    <div className="font-medium text-red-900 flex items-center gap-1">
                      <AlertTriangle className="h-4 w-4" />
                      No tasks defined - needs attention
                    </div>
                  </div>
                ) : null}

                {/* Task Breakdown */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center text-gray-600">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {caseItem.completed_tasks} done
                    </span>
                    <span className="flex items-center text-yellow-600">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      {caseItem.in_progress_tasks} in progress
                    </span>
                    <span className="text-gray-500">
                      {caseItem.todo_tasks} to do
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {caseProgress.length > 4 && (
              <div className="text-center pt-4 border-t border-gray-200">
                <button 
                  onClick={handleViewAllClick}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                >
                  View {caseProgress.length - 4} more cases
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseProgressWidget;